/* <proconex-3d> — simulateur 3D Proconex Lévesque.
   Attributs : kind (cabanon|garage) · w · d (pi) · wall (pi) · framing (2x4|2x6)
               · roof (type) · pitch (4|6|8|12) · siding (matériau) · color
               · roofmat · roofcolor · door · windows (0-4) · extras (liste, virgules)
   Tout est synchronisé : la pente change la hauteur du faîte, le colombage change
   l'épaisseur des murs et des ébrasements, la porte de garage s'adapte à la largeur. */
(() => {
  const F = 0.3048;

  const COLORS = {
    "Noir": 0x0A0A0C, "Charbon": 0x2E3134, "Gris ardoise": 0x5A6167, "Blanc": 0xE9E9E6,
    "Beige": 0xC9BA9E, "Sable": 0xD8C9AC, "Brun commercial": 0x4A3A2C, "Vert forêt": 0x2C4034,
    "Bleu acier": 0x33475B, "Rouge grange": 0x7C2B25, "Bois naturel": 0xB08850
  };
  const TRIM_HEX = {
    "Blanc": 0xE8E8E4, "Noir": 0x131315, "Charbon": 0x2E3134,
    "Brun commercial": 0x4A3A2C, "Amande": 0xD9CDB4
  };
  const ROOF_COLORS = {
    "Bardeau noir": 0x1B1C1F, "Bardeau gris": 0x4A4E52, "Bardeau brun": 0x3B2F26,
    "Tôle noire": 0x151619, "Tôle brune": 0x40301F, "Tôle verte": 0x22392B,
    "Tôle rouge": 0x7E241F, "Tôle galvanisée": 0x9AA0A6, "Tôle blanche": 0xDCDCD6
  };

  // pente en /12 → facteur de montée
  const PITCH = { "4": 4 / 12, "5": 5 / 12, "6": 6 / 12, "8": 8 / 12, "10": 10 / 12, "12": 12 / 12 };

  function texture(THREE, hex, mat) {
    const c = document.createElement("canvas"); c.width = 256; c.height = 256;
    const x = c.getContext("2d");
    const base = "#" + hex.toString(16).padStart(6, "0");
    x.fillStyle = base; x.fillRect(0, 0, 256, 256);

    if (mat === "Brique") {
      const bh = 21, bw = 52;
      x.fillStyle = "rgba(0,0,0,.30)";
      for (let r = 0, i = 0; r < 256; r += bh, i++) {
        x.fillRect(0, r + bh - 3, 256, 3);
        const off = (i % 2) ? bw / 2 : 0;
        for (let cx = off; cx < 256; cx += bw) x.fillRect(cx, r, 3, bh);
      }
      x.fillStyle = "rgba(255,255,255,.05)";
      for (let r = 0; r < 256; r += bh) x.fillRect(0, r + 1, 256, 4);
    } else if (mat === "Tôle d'acier H") {
      // toiture à joint debout : pans larges, joints étroits en relief, aucune vis apparente
      const P = 256 / 6, S = 5.2;
      const lumH = ((hex >> 16 & 255) * .299 + (hex >> 8 & 255) * .587 + (hex & 255) * .114) / 255;
      const hi = lumH < .28 ? .30 : .46, hi2 = lumH < .28 ? .13 : .22;
      for (let i = 0; i < 256; i += P) {
        // le pan : légèrement plus clair au centre, assombri près des joints
        const pan = x.createLinearGradient(0, i, 0, i + P);
        pan.addColorStop(0.00, "rgba(0,0,0,.24)");
        pan.addColorStop(0.16, "rgba(255,255,255," + (hi2 * .5).toFixed(3) + ")");
        pan.addColorStop(0.46, "rgba(255,255,255," + hi2.toFixed(3) + ")");
        pan.addColorStop(0.78, "rgba(0,0,0,.10)");
        pan.addColorStop(1.00, "rgba(0,0,0,.30)");
        x.fillStyle = pan; x.fillRect(0, i, 256, P);

        // ombre portée du joint sur le pan
        const sh = x.createLinearGradient(0, i + P - S - 7, 0, i + P - S);
        sh.addColorStop(0, "rgba(0,0,0,0)"); sh.addColorStop(1, "rgba(0,0,0,.42)");
        x.fillStyle = sh; x.fillRect(0, i + P - S - 7, 256, 7);

        // le joint debout : flanc sombre, arête vive, flanc éclairé
        x.fillStyle = "rgba(0,0,0,.62)"; x.fillRect(0, i + P - S, 256, 1.8);
        x.fillStyle = "rgba(255,255,255," + hi.toFixed(3) + ")"; x.fillRect(0, i + P - S + 1.8, 256, 1.5);
        x.fillStyle = "rgba(255,255,255," + (hi * .42).toFixed(3) + ")"; x.fillRect(0, i + P - S + 3.3, 256, 1.1);
        x.fillStyle = "rgba(0,0,0,.34)"; x.fillRect(0, i + P - 0.6, 256, 0.6);
      }
      // très légères ondulations dans la tôle (oil-canning) et grain de la peinture
      for (let k = 0; k < 26; k++) {
        const ox = Math.random() * 256, oy = Math.random() * 256;
        const g2 = x.createRadialGradient(ox, oy, 0, ox, oy, 30 + Math.random() * 26);
        g2.addColorStop(0, "rgba(255,255,255,.035)"); g2.addColorStop(1, "rgba(255,255,255,0)");
        x.fillStyle = g2; x.fillRect(ox - 60, oy - 60, 120, 120);
      }
      x.fillStyle = "rgba(0,0,0,.05)";
      for (let u = 0; u < 256; u += 3) x.fillRect(u, 0, 1, 256);
    } else if (mat === "Tôle d'acier") {
      // profil nervuré : chaque période = plat + nervure relevée, avec ombre et reflet
      const P = 32, rib = 9;
      const lum0 = ((hex >> 16 & 255) * .299 + (hex >> 8 & 255) * .587 + (hex & 255) * .114) / 255;
      for (let i = 0; i < 256; i += P) {
        const g = x.createLinearGradient(i, 0, i + P, 0);
        g.addColorStop(0.00, "rgba(0,0,0,.34)");
        g.addColorStop(0.10, "rgba(0,0,0,.06)");
        g.addColorStop(0.42, "rgba(255,255,255,0)");
        g.addColorStop(0.58, "rgba(0,0,0,.30)");
        g.addColorStop(0.72, "rgba(255,255,255," + (lum0 < .25 ? .16 : .34) + ")");
        g.addColorStop(0.86, "rgba(255,255,255," + (lum0 < .25 ? .05 : .12) + ")");
        g.addColorStop(1.00, "rgba(0,0,0,.34)");
        x.fillStyle = g; x.fillRect(i, 0, P, 256);
        // arête vive de la nervure
        x.fillStyle = "rgba(255,255,255," + (lum0 < .25 ? .20 : .40) + ")";
        x.fillRect(i + P * 0.70, 0, 1.5, 256);
        x.fillStyle = "rgba(0,0,0,.42)";
        x.fillRect(i + P * 0.56, 0, 1.5, 256);
      }
      // vis autoperceuses dans le creux, rangées régulières
      for (let ry = 26; ry < 256; ry += 62) {
        for (let i = 0; i < 256; i += P) {
          const cx = i + P * 0.24, cy = ry;
          x.beginPath(); x.arc(cx, cy + 1.4, 2.6, 0, 6.284);
          x.fillStyle = "rgba(0,0,0,.40)"; x.fill();
          x.beginPath(); x.arc(cx, cy, 2.4, 0, 6.284);
          x.fillStyle = lum0 < .25 ? "rgba(150,155,162,.85)" : "rgba(120,125,132,.75)"; x.fill();
          x.beginPath(); x.arc(cx - .7, cy - .7, 1.1, 0, 6.284);
          x.fillStyle = "rgba(255,255,255,.5)"; x.fill();
        }
      }
      // légères variations de panneau
      x.fillStyle = "rgba(0,0,0,.07)";
      for (let i = 0; i < 256; i += 128) x.fillRect(i, 0, 2, 256);
    } else if (mat === "Contreplaqué peint") {
      x.strokeStyle = "rgba(0,0,0,.15)"; x.lineWidth = 2;
      for (let i = 0; i < 256; i += 64) { x.beginPath(); x.moveTo(i, 0); x.lineTo(i, 256); x.stroke(); }
    } else if (mat === "Pierre décorative") {
      x.fillStyle = "rgba(0,0,0,.26)";
      for (let r = 0; r < 256; r += 30) for (let cx = (r / 30 % 2 ? 18 : 0); cx < 256; cx += 62) {
        x.fillRect(cx, r + 25, 58, 3); x.fillRect(cx + 58, r, 3, 28);
      }
    } else if (mat === "Membrane") {
      // pare-air : chevauchements horizontaux et lattes verticales
      x.fillStyle = "rgba(0,0,0,.07)";
      for (let r = 34; r < 256; r += 68) x.fillRect(0, r, 256, 2);
      x.fillStyle = "rgba(255,255,255,.10)";
      for (let r = 0; r < 256; r += 68) x.fillRect(0, r, 256, 5);
      x.font = "600 9px sans-serif"; x.fillStyle = "rgba(115,110,95,.38)";
      for (let r = 22; r < 256; r += 68) for (let cx = 6; cx < 256; cx += 88) x.fillText("PARE-AIR", cx, r);
      x.fillStyle = "rgba(0,0,0,.22)";
      for (let cx = 14; cx < 256; cx += 42) x.fillRect(cx, 0, 7, 256);
      x.fillStyle = "rgba(255,255,255,.16)";
      for (let cx = 14; cx < 256; cx += 42) x.fillRect(cx, 0, 2, 256);
    } else { // Canexel, déclin de vinyle, déclin d'acier, pin embouveté, bois traité
      const step = mat === "Pin embouveté" ? 16 : 22;
      const lum = ((hex >> 16 & 255) * .299 + (hex >> 8 & 255) * .587 + (hex & 255) * .114) / 255;
      const dark = lum < .2;
      // Revêtements foncés (noir, charbon) : des joints noirs sur du noir sont invisibles. On garde la
      // teinte de base intacte — le noir reste noir — et on rend le relief lisible par une arête claire
      // fine sous chaque joint et un léger dégradé par planche, comme la lumière rasante le ferait.
      x.strokeStyle = dark ? "rgba(0,0,0,.55)" : "rgba(0,0,0,.26)"; x.lineWidth = 2;
      for (let i = 0; i < 256; i += step) { x.beginPath(); x.moveTo(0, i); x.lineTo(256, i); x.stroke(); }
      for (let i = 0; i < 256; i += step) {
        if (dark) {
          const g = x.createLinearGradient(0, i, 0, i + step);
          g.addColorStop(0.00, "rgba(255,255,255,.085)");
          g.addColorStop(0.35, "rgba(255,255,255,.03)");
          g.addColorStop(1.00, "rgba(0,0,0,.18)");
          x.fillStyle = g; x.fillRect(0, i, 256, step);
          x.fillStyle = "rgba(255,255,255,.11)"; x.fillRect(0, i + 2, 256, 1);
        } else {
          x.fillStyle = "rgba(255,255,255,.06)"; x.fillRect(0, i + 2, 256, Math.max(4, step / 4));
        }
      }
    }

    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return t;
  }

  class Proconex3D extends HTMLElement {
    static get observedAttributes() {
      return ["kind", "w", "d", "wall", "framing", "roof", "pitch", "siding", "color",
              "roofmat", "roofcolor", "door", "windows", "extras", "overhang",
              "gdoors", "gdoorw", "service", "doorcolor", "servicecolor", "wincolor", "winstyle", "doorside",
              "doorpos", "svcpos", "winpos", "finish", "svcside", "overhangcolor", "gdoorh", "svcw", "svch", "winside", "winside2", "winpos2",
              "windowsdata", "winsill"];
    }

    connectedCallback() {
      if (this._booted) return;
      this._booted = true;
      this.style.display = "block";
      this.style.position = "relative";
      if (!this.style.height) this.style.height = "100%";
      if (!this.style.minHeight) this.style.minHeight = "440px";
      this._boot();
    }

    disconnectedCallback() {
      // Nettoyage complet : l'éditeur de thème Shopify déplace/remonte les sections, et un composant
      // retiré ne doit plus rien écouter ni rendre. Le prochain connectedCallback redémarre proprement.
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = 0;
      if (this._ro) { this._ro.disconnect(); this._ro = null; }
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this._onVis) { document.removeEventListener("visibilitychange", this._onVis); this._onVis = null; }
      if (this._scene) {
        this._scene.traverse(o => { if (o.geometry) o.geometry.dispose(); const ms = Array.isArray(o.material) ? o.material : (o.material ? [o.material] : []); ms.forEach(x => { if (x.map) x.map.dispose(); x.dispose(); }); });
      }
      if (this._r) {
        const c = this._r.domElement;
        if (this._onLost) { c.removeEventListener("webglcontextlost", this._onLost); c.removeEventListener("webglcontextrestored", this._onRestored); }
        this._r.dispose();
        if (c && c.parentNode === this) this.removeChild(c);
      }
      this._r = null; this._scene = null; this._cam = null; this._model = null; this._booted = false;
    }

    attributeChangedCallback(n, o, v) {
      if (!this._scene || o === v) return;
      if (n === "w" || n === "d" || n === "wall" || n === "roof" || n === "pitch" || n === "overhang") this._sizeDirty = true;
      if (this._rbQueued) return;
      this._rbQueued = true;
      queueMicrotask(() => {
        this._rbQueued = false;
        const sz = this._sizeDirty; this._sizeDirty = false;
        this._rebuild(sz);
      });
    }

    async _boot() {
      let THREE;
      // Import par URL absolue (aucune importmap) : une importmap placée dans le corps de page est
      // ignorée par Chrome dès qu'un script module a été traité avant elle — ce que les scripts
      // injectés par Shopify dans <head> peuvent provoquer. Deux CDN, l'un en secours de l'autre.
      const SRC = ["https://unpkg.com/three@0.184.0/build/three.module.js", "https://cdn.jsdelivr.net/npm/three@0.184.0/build/three.module.js"];
      for (let i = 0; i < SRC.length && !THREE; i++) { try { THREE = await import(/* @vite-ignore */ SRC[i]); } catch (e) {} }
      if (!THREE || !this.isConnected) { if (!THREE) this._fail(); return; }
      this.THREE = THREE;

      const r = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      r.setPixelRatio(Math.min(devicePixelRatio, 2));
      r.shadowMap.enabled = true;
      r.shadowMap.type = THREE.PCFSoftShadowMap;
      r.domElement.style.cssText = "display:block;width:100%;height:100%;cursor:grab;touch-action:none";
      this.appendChild(r.domElement);
      this._r = r;
      // Perte de contexte WebGL (onglet en arrière-plan, pression mémoire mobile) : on retient la
      // boucle, puis on reconstruit et on reprend dès que le navigateur rend le contexte.
      this._onLost = (e) => { e.preventDefault(); if (this._raf) cancelAnimationFrame(this._raf); this._raf = 0; };
      this._onRestored = () => { try { this._rebuild(true); } catch (e) {} if (!this._raf && this._loop) this._loop(); };
      r.domElement.addEventListener("webglcontextlost", this._onLost, false);
      r.domElement.addEventListener("webglcontextrestored", this._onRestored, false);

      const scene = new THREE.Scene();
      this._scene = scene;
      const cam = new THREE.PerspectiveCamera(36, 1, 0.1, 400);
      this._cam = cam;

      scene.add(new THREE.HemisphereLight(0xdfe6ef, 0x1c1e22, 1.05));
      // lumière de contre-jour discrète : sépare les arêtes d'un volume noir du fond sombre
      const rim = new THREE.DirectionalLight(0x9fb4cc, 0.55); rim.position.set(6, 7, -11); scene.add(rim);
      const sun = new THREE.DirectionalLight(0xfff2d8, 2.0);
      sun.position.set(-9, 13, 8);
      sun.castShadow = true;
      sun.shadow.mapSize.set(2048, 2048);
      const s = sun.shadow.camera;
      s.left = -16; s.right = 16; s.top = 16; s.bottom = -16; s.far = 60;
      scene.add(sun);
      const fill = new THREE.DirectionalLight(0xbcd0e6, 0.5);
      fill.position.set(7, 5, -8); scene.add(fill);

      const ground = new THREE.Mesh(
        new THREE.CircleGeometry(30, 64),
        // sol légèrement plus clair et plus chaud que le fond de page : un bâtiment noir s'en détache
        new THREE.MeshStandardMaterial({ color: 0x1b1b1e, roughness: 1, metalness: 0 })
      );
      ground.rotation.x = -Math.PI / 2;
      ground.position.y = -0.005;
      ground.receiveShadow = true;
      scene.add(ground);

      const grid = new THREE.GridHelper(40, 40, 0x35353a, 0x232326);
      grid.position.y = 0.002;
      scene.add(grid);

      this._model = new THREE.Group();
      scene.add(this._model);

      this._yaw = -0.62; this._pitchCam = 0.30; this._dist = 20;
      this._target = new THREE.Vector3(0, 1.2, 0);
      this._drag = null;

      const el = r.domElement;
      /* Un doigt : rotation. Deux doigts : pincement pour zoomer (écartement = distance caméra), la rotation
         est suspendue pendant le pincement. La roulette reste le zoom souris. */
      this._pts = new Map();
      this._pinch = null;
      const pinchDist = () => {
        const p = Array.from(this._pts.values());
        return Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
      };
      el.addEventListener("pointerdown", (e) => {
        this._pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        try { el.setPointerCapture(e.pointerId); } catch (err) {}
        if (this._pts.size === 2) { this._drag = null; this._pinch = { d: pinchDist(), dist: this._dist }; }
        else if (this._pts.size === 1) { this._drag = { x: e.clientX, y: e.clientY }; el.style.cursor = "grabbing"; }
      });
      el.addEventListener("pointermove", (e) => {
        if (this._pts.has(e.pointerId)) this._pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (this._pinch && this._pts.size >= 2) {
          const d = pinchDist();
          if (d > 0 && this._pinch.d > 0) {
            /* écarter les doigts = se rapprocher du bâtiment ; bornes identiques à la molette (7 à 46) */
            this._dist = Math.max(7, Math.min(46, this._pinch.dist * (this._pinch.d / d)));
            this._place();
          }
          return;
        }
        if (!this._drag) return;
        this._yaw -= (e.clientX - this._drag.x) * 0.007;
        this._pitchCam = Math.max(0.04, Math.min(1.15, this._pitchCam + (e.clientY - this._drag.y) * 0.005));
        this._drag = { x: e.clientX, y: e.clientY };
        this._place();
      });
      const endPointer = (e) => {
        this._pts.delete(e.pointerId);
        try { el.releasePointerCapture(e.pointerId); } catch (err) {}
        if (this._pts.size < 2) this._pinch = null;
        if (this._pts.size === 1) { const p = this._pts.values().next().value; this._drag = { x: p.x, y: p.y }; }
        else if (this._pts.size === 0) { this._drag = null; el.style.cursor = "grab"; }
      };
      el.addEventListener("pointerup", endPointer);
      el.addEventListener("pointercancel", endPointer);
      el.addEventListener("wheel", (e) => {
        e.preventDefault();
        this._dist = Math.max(7, Math.min(46, this._dist + e.deltaY * 0.02));
        this._place();
      }, { passive: false });

      this._ro = new ResizeObserver(() => this._resize());
      this._ro.observe(this);
      this._resize();
      this._rebuild(true);

      this._visible = true;
      if ("IntersectionObserver" in window) {
        this._io = new IntersectionObserver((es) => {
          es.forEach(e => {
            const was = this._visible;
            this._visible = e.isIntersecting;
            if (this._visible && !was && !this._raf) loop();
          });
        }, { rootMargin: "120px 0px" });
        this._io.observe(this);
      }
      this._onVis = () => { if (!document.hidden && this._visible && !this._raf && this._r) loop(); };
      document.addEventListener("visibilitychange", this._onVis);
      const loop = () => {
        if (!this._r) { this._raf = 0; return; } // composant démonté
        if (document.hidden || !this._visible) { this._raf = 0; return; } // en pause hors écran — reprise via IO/visibilitychange
        this._raf = requestAnimationFrame(loop);
        if (!this._drag && this.hasAttribute("autorotate")) { this._yaw += 0.0016; this._place(); }
        r.render(scene, cam);
      };
      this._loop = loop;
      loop();
    }

    _fail() {
      this.innerHTML = '<div style="position:absolute;inset:0;display:flex;align-items:center;' +
        'justify-content:center;text-align:center;font:500 11px/1.6 ui-monospace,monospace;' +
        'letter-spacing:.12em;color:#5A5A63;padding:24px">MODÈLE 3D INDISPONIBLE<br>SUR CET APPAREIL</div>';
    }

    _resize() {
      if (!this._r) return;
      const w = this.clientWidth || 800, h = this.clientHeight || 460;
      const dpr = Math.min(devicePixelRatio || 1, 2);
      if (this._r.getPixelRatio() !== dpr) this._r.setPixelRatio(dpr); // zoom / changement d'écran
      this._r.setSize(w, h, false);
      this._cam.aspect = w / h;
      this._cam.updateProjectionMatrix();
      this._place();
    }

    _place() {
      const c = this._cam, d = this._dist;
      c.position.set(
        this._target.x + d * Math.cos(this._pitchCam) * Math.sin(this._yaw),
        this._target.y + d * Math.sin(this._pitchCam),
        this._target.z + d * Math.cos(this._pitchCam) * Math.cos(this._yaw)
      );
      c.lookAt(this._target);
    }

    /* RC6 — permet au panneau Shopify de cadrer automatiquement la face modifiée. */
    focusSide(side) {
      if (!this._cam) return;
      const map = { 'Avant':0, 'Arrière':Math.PI, 'Côté gauche':-Math.PI/2, 'Côté droit':Math.PI/2 };
      const targetYaw = Object.prototype.hasOwnProperty.call(map, side) ? map[side] : 0;
      const fromYaw = this._yaw, fromPitch = this._pitchCam;
      let delta = targetYaw - fromYaw;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      const t0 = performance.now(), dur = 360;
      const ease = t => 1 - Math.pow(1 - t, 3);
      const step = now => {
        const p = Math.max(0, Math.min(1, (now - t0) / dur)), e = ease(p);
        this._yaw = fromYaw + delta * e;
        this._pitchCam = fromPitch + (0.28 - fromPitch) * e;
        this._place();
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }

    _a(n, dflt) { const v = this.getAttribute(n); return v === null || v === "" ? dflt : v; }

    _rebuild(refit) {
      const THREE = this.THREE, m = this._model;
      m.traverse(o => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach(x => {
          if (x.map) x.map.dispose(); x.dispose();
        });
      });
      m.clear();

      const kind = this._a("kind", "cabanon");
      const W = parseFloat(this._a("w", kind === "garage" ? 20 : 12)) * F;
      const D = parseFloat(this._a("d", kind === "garage" ? 24 : 16)) * F;
      const H = parseFloat(this._a("wall", kind === "garage" ? 9 : 8)) * F;
      const framing = this._a("framing", "2x6");
      const wallT = (framing === "2x6" ? 5.5 : 3.5) * 0.0254;
      const roofType = this._a("roof", "Deux versants");
      const isCathType = roofType === "Cathédrale" || /truss/i.test(roofType);
      const pitch = PITCH[this._a("pitch", "6")] || PITCH["6"];
      const sidingMat = this._a("siding", "Canexel");
      const sidingHex = COLORS[this._a("color", "Charbon")] ?? COLORS["Charbon"];
      const roofMat = this._a("roofmat", "Bardeau");
      const roofHex = ROOF_COLORS[this._a("roofcolor", "Bardeau noir")] ?? ROOF_COLORS["Bardeau noir"];
      const doorSpec = this._a("door", kind === "garage" ? "Porte de garage 9 pi" : "Porte simple 34 po");
      const nWin = Math.max(0, Math.min(4, parseInt(this._a("windows", kind === "garage" ? 1 : 2), 10) || 0));
      const extras = this._a("extras", "").split(",").map(s => s.trim()).filter(Boolean);
      const nGDoors = Math.max(0, Math.min(2, parseInt(this._a("gdoors", "1"), 10) || 0));
      const gDoorFeet = parseFloat(/([\d.]+)/.exec(this._a("gdoorw", "9 pi"))?.[1] || 9);
      const hasService = /oui/i.test(this._a("service", "Oui"));
      const doorHex = TRIM_HEX[this._a("doorcolor", "Blanc")] ?? TRIM_HEX["Blanc"];
      const svcHex = TRIM_HEX[this._a("servicecolor", "Blanc")] ?? TRIM_HEX["Blanc"];
      const winHex = TRIM_HEX[this._a("wincolor", "Blanc")] ?? TRIM_HEX["Blanc"];
      const winStyle = this._a("winstyle", "Coulissante");

      const noFinish = /sans/i.test(this._a("finish", "Avec finition"));
      const texKey = noFinish ? "Membrane" : sidingMat;
      const sideTex = texture(THREE, noFinish ? 0xE3DED2 : sidingHex, texKey);
      sideTex.repeat.set(texKey === "Tôle d'acier" ? Math.max(1, W / F / 8) : Math.max(1, W / F / 3), texKey === "Tôle d'acier" ? 1 : Math.max(1, H / F / 3));
      const sideMat = new THREE.MeshStandardMaterial({
        map: sideTex, color: 0xffffff,
        // un peu moins mat pour les teintes très foncées : le soleil dessine alors les arêtes du volume
        roughness: noFinish ? 0.92 : (sidingMat === "Tôle d'acier" ? 0.42 : sidingMat === "Déclin d'acier" ? 0.5 : (((sidingHex >> 16 & 255) * .299 + (sidingHex >> 8 & 255) * .587 + (sidingHex & 255) * .114) / 255 < .2 ? 0.7 : 0.86)),
        metalness: !noFinish && (sidingMat === "Tôle d'acier" || sidingMat === "Déclin d'acier") ? 0.42 : 0.04
      });
      const isSteelRoof = roofMat === "Tôle d'acier";
      const roofTex = isSteelRoof ? texture(THREE, roofHex, "Tôle d'acier H") : null;
      if (roofTex) roofTex.repeat.set(1, Math.max(1, Math.round(D / F / 8)));
      const roofSurface = new THREE.MeshStandardMaterial({
        color: isSteelRoof ? 0xffffff : roofHex,
        map: roofTex,
        roughness: roofMat === "Tôle d'acier" ? 0.26 : 0.9,
        metalness: roofMat === "Tôle d'acier" ? 0.68 : 0.05
      });
      const trim = new THREE.MeshStandardMaterial({ color: 0xF2F2EE, roughness: 0.7 });
      const doorMat = new THREE.MeshStandardMaterial({ color: doorHex, roughness: 0.62, metalness: 0.12 });
      const doorTrim = new THREE.MeshStandardMaterial({ color: doorHex, roughness: 0.5 });
      const svcMat = new THREE.MeshStandardMaterial({ color: svcHex, roughness: 0.62 });
      const winTrim = new THREE.MeshStandardMaterial({ color: winHex, roughness: 0.6 });
      const dark = new THREE.MeshStandardMaterial({ color: 0x121214, roughness: 0.75 });
      const glass = new THREE.MeshStandardMaterial({
        color: 0x8FB4CC, roughness: 0.12, metalness: 0.62,
        transparent: true, opacity: 0.62
      });
      const slab = new THREE.MeshStandardMaterial({ color: 0x54565A, roughness: 0.96 });
      const accent = new THREE.MeshStandardMaterial({ color: 0xFFC20E, roughness: 0.5 });

      const box = (w, h, d, mat, x, y, z) => {
        const b = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
        b.position.set(x, y, z); b.castShadow = true; b.receiveShadow = true;
        m.add(b); return b;
      };

      // dalle
      const slabH = 0.14;
      box(W + 0.34, slabH, D + 0.34, slab, 0, slabH / 2, 0);
      const y0 = slabH;

      // hauteur du faîte selon le type de toit
      let rise = 0;
      if (roofType === "Deux versants") rise = (W / 2) * pitch;
      else if (isCathType) rise = (W / 2) * Math.max(pitch, 8 / 12) * 1.25;
      else if (roofType === "Toit 4 pentes") rise = (W / 2) * pitch;
      else if (roofType === "Une pente") rise = W * pitch * 0.62;
      else rise = W * 0.045; // toit plat

      // murs — quatre panneaux, épaisseur réelle selon le colombage
      const wallY = y0 + H / 2;
      box(W, H, wallT, sideMat, 0, wallY, D / 2 - wallT / 2);
      box(W, H, wallT, sideMat, 0, wallY, -D / 2 + wallT / 2);
      box(wallT, H, D - wallT * 2, sideMat, -W / 2 + wallT / 2, wallY, 0);
      box(wallT, H, D - wallT * 2, sideMat, W / 2 - wallT / 2, wallY, 0);

      // rangée de fondation pour brique et pierre
      if (!noFinish && (sidingMat === "Brique" || sidingMat === "Pierre décorative")) {
        box(W + 0.06, 0.2, D + 0.06, new THREE.MeshStandardMaterial({ color: 0x3A3C40, roughness: 0.95 }), 0, y0 + 0.1, 0);
      }

      // pignons
      const gable = (z) => {
        const sh = new THREE.Shape();
        sh.moveTo(-W / 2, 0); sh.lineTo(W / 2, 0);
        if (roofType === "Une pente") sh.lineTo(W / 2, rise);
        else if (roofType === "Toit plat") { sh.lineTo(W / 2, rise); sh.lineTo(-W / 2, rise); }
        else sh.lineTo(0, rise);
        sh.closePath();
        const g = new THREE.Mesh(new THREE.ExtrudeGeometry(sh, { depth: wallT, bevelEnabled: false }), sideMat);
        g.position.set(0, y0 + H, z);
        g.castShadow = true; m.add(g);
      };
      if (roofType !== "Toit plat" && roofType !== "Toit 4 pentes") { gable(D / 2 - wallT); gable(-D / 2); }

      // toiture
      const over = (() => {
        const s = String(this._a("overhang", "12 po"));
        const pi = /([\d.]+)\s*pi/.exec(s), po = /([\d.]+)\s*po/.exec(s);
        const feet = pi ? parseFloat(pi[1]) : (po ? parseFloat(po[1]) / 12 : 1);
        return feet * F;
      })();
      const panelT = 0.075;
      if (roofType === "Deux versants" || isCathType) {
        const slope = Math.hypot(W / 2 + over, rise);
        const ang = Math.atan2(rise, W / 2 + over);
        [-1, 1].forEach(sgn => {
          const p = new THREE.Mesh(new THREE.BoxGeometry(slope, panelT, D + over * 2), roofSurface);
          p.position.set(sgn * (W / 4 + over / 2), y0 + H + rise / 2 + 0.02, 0);
          p.rotation.z = sgn * ang * (sgn > 0 ? -1 : 1) * -1;
          p.rotation.z = -sgn * ang;
          p.castShadow = true; p.receiveShadow = true; m.add(p);
        });
        box(0.1, 0.1, D + over * 2, dark, 0, y0 + H + rise + 0.05, 0);
      } else if (roofType === "Toit 4 pentes") {
        const eW = W / 2 + over, eD = D / 2 + over, yE = y0 + H + 0.02, yR = y0 + H + rise + 0.02;
        const alongZ = D >= W;
        const rl = Math.max(0.5, alongZ ? D - W : W - D) / 2;
        const A = alongZ ? [0, yR, -rl] : [-rl, yR, 0];
        const B = alongZ ? [0, yR, rl] : [rl, yR, 0];
        const c1 = [-eW, yE, -eD], c2 = [eW, yE, -eD], c3 = [eW, yE, eD], c4 = [-eW, yE, eD];
        const texScale = F * 8;
        const hipMat = () => {
          if (!isSteelRoof) return roofSurface;
          const t = texture(THREE, roofHex, "Tôle d'acier H");
          t.repeat.set(1, 1);
          return new THREE.MeshStandardMaterial({ color: 0xffffff, map: t, roughness: 0.26, metalness: 0.68, side: THREE.DoubleSide });
        };
        const face = (pts, uAxis, slopeLen) => {
          const tri = [], uvs = [];
          const order = pts.length === 4 ? [0, 1, 2, 0, 2, 3] : [0, 1, 2];
          order.forEach(i => {
            const p = pts[i];
            tri.push(p[0], p[1], p[2]);
            // joints debout : ils descendent la pente → axe des pans (v) = position horizontale
            uvs.push(((p[1] - yE) / Math.max(0.01, yR - yE)) * slopeLen / texScale,
                     (uAxis === "z" ? p[2] + eD : p[0] + eW) / texScale);
          });
          const g2 = new THREE.BufferGeometry();
          g2.setAttribute("position", new THREE.Float32BufferAttribute(tri, 3));
          g2.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
          g2.computeVertexNormals();
          const mesh = new THREE.Mesh(g2, hipMat());
          mesh.material.side = THREE.DoubleSide;
          mesh.castShadow = true; mesh.receiveShadow = true; mesh.name = "Toiture 4 pentes"; m.add(mesh);
        };
        const sSlope = Math.hypot(alongZ ? eW : eD, rise);
        const sHip = Math.hypot((alongZ ? eD : eW) - rl, rise);
        if (alongZ) {
          face([c1, c4, B, A], "z", sSlope);
          face([c2, c3, B, A], "z", sSlope);
          face([c1, c2, A], "x", sHip);
          face([c4, c3, B], "x", sHip);
        } else {
          face([c1, c2, B, A], "x", sSlope);
          face([c4, c3, B, A], "x", sSlope);
          face([c1, c4, A], "z", sHip);
          face([c2, c3, B], "z", sHip);
        }
        box(0.1, 0.1, alongZ ? rl * 2 : 0.1, dark, 0, yR + 0.04, 0);
      } else if (roofType === "Une pente") {
        const slope = Math.hypot(W + over * 2, rise);
        const ang = Math.atan2(rise, W + over * 2);
        const p = new THREE.Mesh(new THREE.BoxGeometry(slope, panelT, D + over * 2), roofSurface);
        p.position.set(0, y0 + H + rise / 2 + 0.02, 0);
        p.rotation.z = ang;
        p.castShadow = true; p.receiveShadow = true; m.add(p);
      } else {
        const p = new THREE.Mesh(new THREE.BoxGeometry(W + over * 2, panelT, D + over * 2), roofSurface);
        p.position.set(0, y0 + H + rise, 0);
        p.rotation.z = -Math.atan2(rise, W);
        p.castShadow = true; p.receiveShadow = true; m.add(p);
      }

      // façade avant : portes, synchronisées avec la largeur
      const fz = D / 2 + 0.012;
      const svcPo = 34; // validé au meeting du 4 septembre
      const svcHPo = 84; // validé au meeting du 4 septembre
      const svcW = (svcPo / 12) * F, svcH = Math.min(H - 0.3, (svcHPo / 12) * F);
      let svcX = 0;

      // ---- façade : on réserve la place de chaque ouverture avant de la poser ----
      const EDGE = 0.28;                    // marge minimale au coin
      // débord réaliste : bordure de fascia le long des rives du toit + soffite sous le débord
      {
        const fasciaHex = /blanc/i.test(this._a("overhangcolor", "Noir")) ? 0xE8E6E0 : 0x17181B;
        const fMat = new THREE.MeshStandardMaterial({ color: fasciaHex, roughness: 0.55, metalness: 0.06 });
        const fH = 0.15, fT = 0.06, yE2 = y0 + H + 0.02;
        const fb = (w, h, d, x, y, z) => { const bx = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), fMat); bx.position.set(x, y, z); bx.castShadow = true; bx.receiveShadow = true; bx.name = "Fascia"; m.add(bx); };
        const isCath2 = roofType === "Cathédrale" || /truss/i.test(roofType);
        if (roofType === "Deux versants" || isCath2) {
          [-1, 1].forEach(sgn => {
            fb(fT, fH, D + over * 2, sgn * (W / 2 + over), yE2 - fH / 2 + 0.03, 0);
            if (over > 0.06) fb(over + 0.02, 0.045, D + over * 2, sgn * (W / 2 + over / 2), yE2 - 0.055, 0);
          });
        } else if (roofType === "Toit 4 pentes") {
          const eW2 = W / 2 + over, eD2 = D / 2 + over, yF = yE2 - fH / 2 + 0.03;
          fb(fT, fH, eD2 * 2 + fT, -eW2, yF, 0); fb(fT, fH, eD2 * 2 + fT, eW2, yF, 0);
          fb(eW2 * 2 + fT, fH, fT, 0, yF, -eD2); fb(eW2 * 2 + fT, fH, fT, 0, yF, eD2);
          if (over > 0.06) {
            fb(over + 0.02, 0.045, eD2 * 2, -(W / 2 + over / 2), yE2 - 0.055, 0);
            fb(over + 0.02, 0.045, eD2 * 2, (W / 2 + over / 2), yE2 - 0.055, 0);
            fb(W, 0.045, over + 0.02, 0, yE2 - 0.055, -(D / 2 + over / 2));
            fb(W, 0.045, over + 0.02, 0, yE2 - 0.055, (D / 2 + over / 2));
          }
        } else if (roofType === "Une pente") {
          fb(fT, fH, D + over * 2, -(W / 2 + over), yE2 - fH / 2 + 0.03, 0);
          fb(fT, fH, D + over * 2, (W / 2 + over), y0 + H + rise - fH / 2 + 0.05, 0);
          if (over > 0.06) fb(over + 0.02, 0.045, D + over * 2, -(W / 2 + over / 2), yE2 - 0.055, 0);
        } else { // toit plat : ceinture périmétrique au niveau du toit
          const yF = y0 + H + rise + 0.01;
          fb(fT, fH, D + over * 2 + fT, -(W / 2 + over), yF, 0); fb(fT, fH, D + over * 2 + fT, (W / 2 + over), yF, 0);
          fb(W + over * 2 + fT, fH, fT, 0, yF, -(D / 2 + over)); fb(W + over * 2 + fT, fH, fT, 0, yF, (D / 2 + over));
        }
      }

      const GAP = 0.24;                     // jeu minimal entre deux ouvertures
      const wallBands = { front: [], back: [], left: [], right: [] };
      const reserveOn = (wall, cx, w) => wallBands[wall].push([cx - w / 2, cx + w / 2]);
      const fitsOn = (wall, lim, cx, w) => {
        if (cx - w / 2 < -lim || cx + w / 2 > lim) return false;
        return wallBands[wall].every(([a, b]) => cx + w / 2 + GAP <= a || cx - w / 2 - GAP >= b);
      };
      // centre libre le plus proche de la position demandée, sur le mur donné
      const findSlotOn = (wall, lim, w, prefer) => {
        const step = 0.04;
        for (let d = 0; d <= lim * 2; d += step) {
          for (const s of (d === 0 ? [0] : [1, -1])) {
            const cx = prefer + s * d;
            if (fitsOn(wall, lim, cx, w)) return cx;
          }
        }
        return null;
      };
      // ---- Règle dure : deux ouvertures d'un même mur ne peuvent pas occuper la même surface ----
      // Calcul UNIQUE, partagé avec le panneau par l'événement proconex:windows-rendered.
      // On compare les EMPREINTES RÉELLEMENT DESSINÉES (cadre inclus), en coordonnées monde le long
      // du mur, sans aucune marge : EDGE et GAP ne servent qu'au rendu et au placement hérité,
      // jamais à décider qu'un emplacement est occupé. EPS est une tolérance numérique, pas un jeu.
      // Une ouverture qui croise une ouverture déjà inscrite (ou dépasse du mur) n'est pas dessinée
      // et est signalée ; sa position demandée reste intacte dans l'état du panneau.
      const EPS = 1e-6;
      const OPENINGS = [];
      const CONFLICT_PAIRS = []; // chaque intersection réelle, avec ses DEUX participants
      // Longueur du mur porteur de l'ouverture : Avant/Arrière = W, Côté gauche/droit = D.
      const wallHalf = (wall) => (wall === "front" || wall === "back") ? W / 2 : D / 2;
      // Les groupes de portes sont tournés : leur x local devient +x, −x, +z ou −z monde selon le mur.
      const doorLocalToWorld = (wall, lx) => wall === "front" ? lx : wall === "back" ? -lx : wall === "left" ? lx : -lx;
      // Rectangle réel de l'ouverture sur son mur : [a,b] horizontal (monde, le long du mur) et
      // [yb,yt] vertical (mètres depuis le sol), tels que dessinés, cadre inclus.
      // Intersection = chevauchement horizontal ET vertical (aire commune non nulle) ; un simple
      // contact bord à bord n'en est pas une (EPS = tolérance numérique seulement).
      const tryOpening = (id, kind, wall, cx, w, yb, yt) => {
        const a = cx - w / 2, b = cx + w / 2, half = wallHalf(wall);
        let hit = null;
        if (a < -half - EPS || b > half + EPS) hit = "wall";
        else {
          // Règle de collision partagée avec le panneau : une seule implémentation, dans
          // proconex-openings.js. Le repli local ne sert que si ce script n'a pas été chargé.
          const PO = window.ProconexOpenings;
          const self = { wall, a, b, yb, yt };
          for (const p of OPENINGS) {
            if (!p.drawn) continue;
            const croise = PO ? PO.overlaps(self, p)
              : (p.wall === wall && a < p.b - EPS && p.a < b - EPS && yb < p.yt - EPS && p.yb < yt - EPS);
            if (croise) { hit = p.id; break; }
          }
        }
        OPENINGS.push({ id, kind, wall, cx, w, a, b, yb, yt, drawn: hit === null, hit });
        if (hit !== null && hit !== "wall") CONFLICT_PAIRS.push([hit, id]);
        return hit === null;
      };
      // Participants : toute ouverture impliquée dans une intersection, des deux côtés — l'ordre de
      // dessin décide seulement laquelle n'est pas affichée, jamais laquelle « a tort ».
      const conflictParticipants = () => { const set = {}; CONFLICT_PAIRS.forEach(pr => { set[pr[0]] = true; set[pr[1]] = true; }); return set; };
      let doorBlocked = false, svcBlocked = false;
      const gdoorBlocked = [];
      // Porte principale réellement dessinée (x local dans le groupe tourné de son mur, largeur,
      // hauteur) : la rampe et l'auvent s'y rattachent, quel que soit le mur choisi.
      let mainDoor = null;
      const reserve = (cx, w) => reserveOn("front", cx, w);
      const fits = (cx, w) => fitsOn("front", W / 2 - EDGE, cx, w);
      const findSlot = (w, prefer) => findSlotOn("front", W / 2 - EDGE, w, prefer);

      const doorSide = this._a("doorside", "Avant");
      const SIDEI = { "Avant": 0, "Arrière": 1, "Côté gauche": 2, "Côté droit": 3 };
      const sideIdx = SIDEI[doorSide] ?? 0;
      const onFront = sideIdx === 0;
      const wallW = (sideIdx <= 1) ? W : D;
      const dgrp = new THREE.Group();
      const dbox = (w, h, d, mat, x, y, z) => {
        const bx = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
        bx.position.set(x, y, z); bx.castShadow = true; bx.receiveShadow = true;
        dgrp.add(bx); return bx;
      };
      const doorWall = sideIdx === 0 ? "front" : sideIdx === 1 ? "back" : sideIdx === 2 ? "left" : "right";
      const svcSide = this._a("svcside", "Avant");
      const svcSideIdx = SIDEI[svcSide] ?? 0;
      const svcWall = svcSideIdx === 0 ? "front" : svcSideIdx === 1 ? "back" : svcSideIdx === 2 ? "left" : "right";
      const keep = (cx, w) => {
        reserveOn(doorWall, cx, w);
        if (sideIdx > 0 && Math.abs(cx) > 0.01) reserveOn(doorWall, -cx, w); // murs tournés : l'axe local peut être inversé
      };

      if (kind === "garage") {
        const ghFt = Math.max(6, Math.min(12, parseFloat(this._a("gdoorh", "7")) || 7));
        const dh = Math.min(H - 0.4, ghFt * F);
        const svcP = Math.max(0, Math.min(100, parseFloat(this._a("svcpos", "85")) || 0));
        const svcWallW = (svcSideIdx <= 1) ? W : D;
        const svcTarget = -svcWallW / 2 + EDGE + svcW / 2 + Math.max(0, svcWallW - 2 * EDGE - svcW) * (svcP / 100);
        const n = Math.max(0, Math.min(2, nGDoors));
        if (n > 0) {
          // Largeur DEMANDÉE, jamais réduite en silence : si elle ne tient pas sur le mur, le registre
          // d'ouvertures le signale (hit = "wall") et la porte n'est pas dessinée en chevauchement.
          const w = Math.max(0.9, gDoorFeet * F);
          const spanTotal = n * w + (n - 1) * GAP;
          const leftEdge = -wallW / 2 + EDGE;
          const rightEdge = wallW / 2 - EDGE;
          const posP = Math.max(0, Math.min(100, parseFloat(this._a("doorpos", "50")) || 0));
          const start = leftEdge + Math.max(0, (rightEdge - leftEdge) - spanTotal) * (posP / 100) + w / 2;
          try { window.__cfgDbg = window.__cfgDbg || {}; window.__cfgDbg.gdoors = { posP, start: +start.toFixed(2), free: +((rightEdge - leftEdge) - spanTotal).toFixed(2) }; } catch (e) {}
          for (let i = 0; i < n; i++) {
            const px = start + i * (w + GAP);
            keep(px, w + 0.14);
            if (!tryOpening("gdoor" + (i + 1), "gdoor", doorWall, doorLocalToWorld(doorWall, px), w + 0.14, y0, y0 + dh + 0.14)) { gdoorBlocked.push(i); continue; }
            dbox(w + 0.14, dh + 0.14, 0.05, doorTrim, px, y0 + (dh + 0.14) / 2, 0.012);
            if (!mainDoor) mainDoor = { lx: px, w: w + 0.14, h: dh + 0.14 };
            const panels = 4;
            for (let j = 0; j < panels; j++) {
              dbox(w, dh / panels - 0.025, 0.05, doorMat, px, y0 + (j + 0.5) * (dh / panels), 0.042);
            }
            dbox(w * 0.9, 0.05, 0.05, accent, px, y0 + dh * 0.78, 0.072);
          }
          svcX = svcTarget;
        } else {
          svcX = svcTarget;
        }
      } else if (/garage/i.test(doorSpec)) {
        const gf = parseFloat(/([\d.]+)\s*pi/.exec(doorSpec)?.[1] || 8);
        const gw = Math.min(wallW - 2 * EDGE - 0.3, gf * F);
        const ghFt2 = Math.max(6, Math.min(12, parseFloat(this._a("gdoorh", "7")) || 7));
        const gh = Math.min(H - 0.35, ghFt2 * F);
        const posPg = Math.max(0, Math.min(100, parseFloat(this._a("doorpos", "50")) || 0));
        const limG = Math.max(0, wallW / 2 - EDGE - (gw + 0.16) / 2);
        const gx = limG * ((posPg - 50) / 50);
        try { window.__cfgDbg = window.__cfgDbg || {}; window.__cfgDbg.cabDoor = { posPg, gx: +gx.toFixed(2), limG: +limG.toFixed(2) }; } catch (e) {}
        keep(gx, gw + 0.16);
        doorBlocked = !tryOpening("door", "door", doorWall, doorLocalToWorld(doorWall, gx), gw + 0.16, y0, y0 + gh + 0.16);
        if (!doorBlocked) {
          dbox(gw + 0.16, gh + 0.16, 0.05, doorTrim, gx, y0 + (gh + 0.16) / 2, 0.012);
          mainDoor = { lx: gx, w: gw + 0.16, h: gh + 0.16 };
          dbox(gw, gh, 0.06, doorMat, gx, y0 + gh / 2, 0.042);
          const panels = 4;
          for (let i = 1; i < panels; i++) {
            dbox(gw - 0.06, 0.025, 0.02, doorTrim, gx, y0 + (gh * i) / panels, 0.082);
          }
          if (/vitr/i.test(doorSpec)) {
            const lites = 3, lw = gw / (lites + 1.6), lh = Math.min(0.34, gh / 5.5);
            for (let i = 0; i < lites; i++) {
              dbox(lw, lh, 0.02, glass, gx + (i - (lites - 1) / 2) * (lw * 1.24), y0 + gh * 0.72, 0.092);
            }
          }
        }
        svcX = null;
      } else {
        const isDouble = /double/i.test(doorSpec);
        const dw = isDouble ? 1.52 : (/50/.test(doorSpec) ? 1.27 : /34/.test(doorSpec) ? 0.86 : 0.92);
        const dh = Math.min(H - 0.4, 2.03);
        const posPd = Math.max(0, Math.min(100, parseFloat(this._a("doorpos", "50")) || 0));
        const limD = Math.max(0, wallW / 2 - EDGE - (dw + 0.12) / 2);
        const dcx = limD * ((posPd - 50) / 50);
        keep(dcx, dw + 0.12);
        doorBlocked = !tryOpening("door", "door", doorWall, doorLocalToWorld(doorWall, dcx), dw + 0.12, y0, y0 + dh + 0.12);
        if (!doorBlocked) {
          dbox(dw + 0.12, dh + 0.12, 0.05, doorTrim, dcx, y0 + (dh + 0.12) / 2, 0.012);
          mainDoor = { lx: dcx, w: dw + 0.12, h: dh + 0.12 };
          dbox(dw, dh, 0.05, doorMat, dcx, y0 + dh / 2, 0.042);
          if (isDouble) dbox(0.03, dh, 0.06, doorTrim, dcx, y0 + dh / 2, 0.062);
          if (/vitr/i.test(doorSpec)) {
            const pw = isDouble ? dw / 2 - 0.14 : dw - 0.2;
            const ph = dh * 0.42;
            if (isDouble) {
              dbox(pw, ph, 0.02, glass, dcx - dw / 4, y0 + dh * 0.68, 0.072);
              dbox(pw, ph, 0.02, glass, dcx + dw / 4, y0 + dh * 0.68, 0.072);
            } else {
              dbox(pw, ph, 0.02, glass, dcx, y0 + dh * 0.68, 0.072);
            }
          }
          dbox(0.07, 0.07, 0.07, accent, dcx + dw / 2 - 0.14, y0 + dh * 0.5, 0.082);
        }
        svcX = null;
      }

      if (sideIdx === 0) dgrp.position.z = D / 2;
      else if (sideIdx === 1) { dgrp.position.z = -D / 2; dgrp.rotation.y = Math.PI; }
      else if (sideIdx === 2) { dgrp.position.x = -W / 2; dgrp.rotation.y = -Math.PI / 2; }
      else { dgrp.position.x = W / 2; dgrp.rotation.y = Math.PI / 2; }
      m.add(dgrp);

      if (hasService && kind !== "garage") {
        const svcP3 = Math.max(0, Math.min(100, parseFloat(this._a("svcpos", "85")) || 0));
        const svcWW3 = (svcSideIdx <= 1) ? W : D;
        svcX = -svcWW3 / 2 + EDGE + svcW / 2 + Math.max(0, svcWW3 - 2 * EDGE - svcW) * (svcP3 / 100);
      }
      if (hasService && svcX !== null) {
        reserveOn(svcWall, svcX, svcW + 0.12);
        if (svcSideIdx > 0 && Math.abs(svcX) > 0.01) reserveOn(svcWall, -svcX, svcW + 0.12);
        // Plus de déplacement silencieux vers un créneau libre : la porte de service est dessinée là
        // où le client l'a demandée, sauf si son empreinte croise réellement une autre ouverture —
        // elle est alors signalée au panneau, jamais déplacée ni retirée de l'état.
        svcBlocked = !tryOpening("service", "service", svcWall, doorLocalToWorld(svcWall, svcX), svcW + 0.12, y0, y0 + svcH + 0.12);
      }
      if (hasService && svcX !== null && !svcBlocked) {
        const sg = new THREE.Group();
        if (svcSideIdx === 0) sg.position.z = D / 2;
        else if (svcSideIdx === 1) { sg.position.z = -D / 2; sg.rotation.y = Math.PI; }
        else if (svcSideIdx === 2) { sg.position.x = -W / 2; sg.rotation.y = -Math.PI / 2; }
        else { sg.position.x = W / 2; sg.rotation.y = Math.PI / 2; }
        const sb = (w, h, d, mat, x, y, z) => {
          const bx = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
          bx.position.set(x, y, z); bx.castShadow = true; bx.receiveShadow = true; sg.add(bx);
        };
        sb(svcW + 0.12, svcH + 0.12, 0.05, svcMat, svcX, y0 + (svcH + 0.12) / 2, 0.012);
        sb(svcW, svcH, 0.05, svcMat, svcX, y0 + svcH / 2, 0.042);
        sb(0.07, 0.07, 0.07, accent, svcX + svcW / 2 - 0.14, y0 + svcH * 0.5, 0.082);
        m.add(sg);
      }

      // fenêtres : la première en façade, les suivantes sur les murs latéraux
      const winW = 0.86, winH = 0.98;
      /* La fenêtre suit réellement la hauteur du mur; sur 12 pi elle monte avec la composition
         tout en conservant une marge minimale au linteau et au plancher. */
      // Hauteur des fenêtres. Architecture en hauteur ABSOLUE : « winsill » (mètres, du plancher au
      // bas de la fenêtre) fixe le centre à sill + winH/2, borné pour garder le linteau sous le haut
      // du mur. Tant que Proconex n'a pas confirmé la hauteur d'appui voulue (notamment sur murs de
      // 12 pi), l'attribut n'est pas envoyé et la formule proportionnelle historique s'applique telle
      // quelle — aucune nouvelle valeur arbitraire n'est introduite ici.
      const sillReq = parseFloat(this._a("winsill", ""));
      const winY = (isFinite(sillReq) && sillReq >= 0)
        ? y0 + Math.min(H - winH / 2 - 0.32, Math.max(winH / 2 + 0.05, sillReq + winH / 2))
        : y0 + Math.min(H - winH / 2 - 0.32, Math.max(winH / 2 + 0.62, H * 0.62));
      const mullions = (mat, w, h, cx, cy, cz, axis) => {
        if (winStyle === "Fixe") return;
        const t = 0.045;
        if (axis === "x") box(0.055, h, t, mat, cx, cy, cz);
        else box(t, h, 0.055, mat, cx, cy, cz);
      };
      // chaque fenêtre a son mur et sa position ; jamais de superposition
      const drawWinAt = (idx, cx) => {
        if (idx <= 1) {
          const zz = (idx === 0 ? 1 : -1) * (D / 2 + 0.012);
          const dir = idx === 0 ? 1 : -1;
          box(winW + 0.12, winH + 0.12, 0.05, winTrim, cx, winY, zz);
          box(winW, winH, 0.05, glass, cx, winY, zz + dir * 0.03);
          mullions(winTrim, winW, winH, cx, winY, zz + dir * 0.05, "x");
        } else {
          const side = idx === 3 ? 1 : -1;
          const xx = side * (W / 2 + 0.012);
          const fr = new THREE.Mesh(new THREE.BoxGeometry(0.05, winH + 0.12, winW + 0.12), winTrim);
          fr.position.set(xx, winY, cx); m.add(fr);
          const gl = new THREE.Mesh(new THREE.BoxGeometry(0.05, winH, winW), glass);
          gl.position.set(xx + side * 0.03, winY, cx); m.add(gl);
          mullions(winTrim, winW, winH, xx + side * 0.05, winY, cx, "z");
        }
      };
      const wallOf = (idx) => idx === 0 ? "front" : idx === 1 ? "back" : idx === 2 ? "left" : "right";
      const wants = [];
      const specFor = (sideName, posVal) => {
        const idx = SIDEI[sideName] ?? 0;
        const lim = ((idx <= 1) ? W : D) / 2 - EDGE;
        const p = Math.max(0, Math.min(100, parseFloat(posVal) || 0));
        return { idx, lim, ask: -(lim - winW / 2) + 2 * (lim - winW / 2) * (p / 100) };
      };
      const spec = (sideAttr, posAttr, defSide, defPos) => specFor(this._a(sideAttr, defSide), this._a(posAttr, defPos));
      // Pont Phase 2 : « windowsdata » porte la collection complète (0 à 4 fenêtres) telle que
      // le panneau la détient. Quand l'attribut est absent ou illisible, on retombe exactement sur
      // l'ancien couple winside/winpos + winside2/winpos2 et sa dérivation, pour les pages anciennes.
      const winData = (() => {
        const raw = this._a("windowsdata", "");
        if (!raw) return null;
        try { const p = JSON.parse(raw); return Array.isArray(p) ? p : null; } catch (e) { return null; }
      })();
      if (winData) {
        winData.slice(0, nWin).forEach(w => wants.push(specFor(w && w.wall, w && w.pos)));
      } else {
        if (nWin > 0) wants.push(spec("winside", "winpos", "Avant", "15"));
        if (nWin > 1) wants.push(spec("winside2", "winpos2", "Côté droit", "50"));
      }
      let placedW = 0;
      const winConflicts = [];
      wants.forEach((wnt, wi) => {
        const wl = wallOf(wnt.idx);
        // Aucune fenêtre demandée n'est déplacée ni retirée de l'état. Elle est dessinée à la position
        // demandée, sauf si son empreinte croise réellement une autre ouverture (règle dure, sans
        // marge) : elle n'est alors pas dessinée à travers l'autre et le panneau est prévenu.
        const free = tryOpening("window" + (wi + 1), "window", wl, wnt.ask, winW + 0.12, winY - (winH + 0.12) / 2, winY + (winH + 0.12) / 2);
        if (!free) winConflicts.push(wi);
        try { window.__cfgDbg = window.__cfgDbg || {}; window.__cfgDbg["win" + (wi + 1)] = { wall: wl, cx: +wnt.ask.toFixed(2), free }; } catch (e) {}
        reserveOn(wl, wnt.ask, winW + 0.12);
        if (free) { drawWinAt(wnt.idx, wnt.ask); placedW++; }
      });
      // Chemin hérité seulement : sans « windowsdata », les fenêtres au-delà de la 2e restent
      // dérivées comme avant. Elles ne sont plus abandonnées quand aucun créneau libre n'est trouvé.
      for (let k = 0; !winData && k < nWin - wants.length; k++) {
        const side = k % 2 === 0 ? 1 : -1;
        const wallN = side > 0 ? "right" : "left";
        const zLimW = D / 2 - EDGE;
        const prefer = (k % 2 === 0 ? -1 : 1) * D * 0.18;
        const found = findSlotOn(wallN, zLimW, winW + 0.12, prefer);
        const zPos = found === null ? prefer : found;
        const freeL = tryOpening("window" + (wants.length + k + 1), "window", wallN, zPos, winW + 0.12, winY - (winH + 0.12) / 2, winY + (winH + 0.12) / 2);
        if (!freeL) winConflicts.push(wants.length + k);
        reserveOn(wallN, zPos, winW + 0.12);
        if (freeL) { drawWinAt(side > 0 ? 3 : 2, zPos); placedW++; }
      }
      // Le panneau et la 3D doivent s'accorder sur le nombre demandé : on le publie.
      try {
        this.dispatchEvent(new CustomEvent("proconex:windows-rendered", {
          detail: (() => {
            const part = conflictParticipants();
            const winIdx = id => { const m = /^window(\d+)$/.exec(id); return m ? parseInt(m[1], 10) - 1 : -1; };
            const partWindows = Object.keys(part).map(winIdx).filter(i => i >= 0).sort((x, y) => x - y);
            return {
              requested: nWin, drawn: placedW, conflicts: winConflicts,
              // représentation partagée : les DEUX participants de chaque intersection (blocked),
              // ce qui n'a pas été dessiné (hidden), et pourquoi (pairs / openings)
              blocked: { windows: partWindows, service: !!part.service, door: !!part.door, gdoors: gdoorBlocked.slice() },
              hidden: OPENINGS.filter(p => !p.drawn).map(p => p.id),
              pairs: CONFLICT_PAIRS.map(pr => pr.slice()),
              openings: OPENINGS.map(p => ({ id: p.id, kind: p.kind, wall: p.wall, cx: +p.cx.toFixed(4), w: +p.w.toFixed(4), yb: +p.yb.toFixed(4), yt: +p.yt.toFixed(4), drawn: p.drawn, hit: p.hit }))
            };
          })()
        }));
      } catch (e) {}

      // extras
      // Auvent et rampe : rattachés à la porte principale, dans le groupe tourné de SON mur (dgrp),
      // donc ils suivent le mur choisi, la position et la largeur de la porte, et tout redimensionnement.
      // Profondeur d'auvent (1,0 m) et longueur/pente de rampe (1,9 m, −0,075 rad) restent les constantes
      // de rendu historiques : les dimensions produit réelles sont à confirmer par Proconex.
      const anchor = mainDoor || { lx: 0, w: Math.min(wallW * 0.55, 2.4), h: H * 0.82 };
      if (extras.some(e => /Auvent/i.test(e))) {
        const aw = anchor.w + 0.6, ay = Math.min(y0 + anchor.h + 0.22, y0 + H - 0.05);
        const a = new THREE.Mesh(new THREE.BoxGeometry(aw, 0.06, 1.0), roofSurface);
        a.position.set(anchor.lx, ay, 0.5);
        a.rotation.x = 0.16; a.castShadow = true; dgrp.add(a);
        [-1, 1].forEach(sg => dbox(0.07, ay - y0, 0.07, trim, anchor.lx + sg * (aw / 2 - 0.1), y0 + (ay - y0) / 2, 0.95));
      }
      if (extras.some(e => /Rampe/i.test(e))) {
        const r = new THREE.Mesh(new THREE.BoxGeometry(anchor.w + 0.3, 0.08, 1.9), trim);
        r.position.set(anchor.lx, y0 - 0.02, 0.95);
        r.rotation.x = -0.075; r.castShadow = true; dgrp.add(r);
      }
      if (extras.some(e => /Lucarne/i.test(e)) && roofType !== "Toit plat") {
        box(1.25, 0.72, 1.05, sideMat, -W * 0.2, y0 + H + rise * 0.44, D * 0.1);
        const cap = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.06, 1.2), roofSurface);
        cap.position.set(-W * 0.2, y0 + H + rise * 0.44 + 0.39, D * 0.1);
        cap.castShadow = true; m.add(cap);
      }
      // Volets : une paire de chaque côté de chaque fenêtre RÉELLEMENT dessinée, sur son propre mur.
      if (extras.some(e => /Volets/i.test(e))) {
        OPENINGS.filter(p => p.kind === "window" && p.drawn).forEach(p => {
          const onX = (p.wall === "front" || p.wall === "back");
          [-1, 1].forEach(sg => {
            const along = p.cx + sg * (winW / 2 + 0.16);
            if (onX) box(0.24, winH + 0.1, 0.04, dark, along, winY, (p.wall === "front" ? 1 : -1) * (D / 2 + 0.02));
            else box(0.04, winH + 0.1, 0.24, dark, (p.wall === "right" ? 1 : -1) * (W / 2 + 0.02), winY, along);
          });
        });
      }
      // Gouttières : uniquement sur les avant-toits que la forme de toit possède réellement.
      //   deux versants / truss : les deux longs côtés · quatre pentes / toit plat : les quatre côtés
      //   une pente : le seul côté bas (−x, cf. la forme du toit)
      if (extras.some(e => /Gouttière/i.test(e))) {
        const gy = y0 + H + 0.03;
        const sideX = (sg) => box(0.09, 0.09, D + over * 1.6, trim, sg * (W / 2 + over * 0.7), gy, 0);
        const sideZ = (sg) => box(W + over * 1.6, 0.09, 0.09, trim, 0, gy, sg * (D / 2 + over * 0.7));
        if (roofType === "Une pente") sideX(-1);
        else if (roofType === "Toit 4 pentes" || roofType === "Toit plat") { sideX(-1); sideX(1); sideZ(-1); sideZ(1); }
        else { sideX(-1); sideX(1); }
      }

      if (refit) {
        const ridge = y0 + H + rise;
        this._target.set(0, ridge * 0.46, 0);
        this._dist = Math.max(W, D) * 1.62 + 5.2;
        this._place();
      }
      this._r.render(this._scene, this._cam);
    }
  }

  if (!customElements.get("proconex-3d")) customElements.define("proconex-3d", Proconex3D);
})();
